class SizeChart extends HTMLElement {
  constructor() {
    super();

    this.chartData   = null;
    this.bodyImages  = [];
    this.currentUnit = 'cm';
    this.currentType = 'flat';
    this.wearType    = 'topwear';

    this.unitLabels = { cm: '(CM)', in: '(Inches)' };
    this.letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

    this.sliderState = { height: 170, chest: 100, waist: 80 };

    this.bodyTypes = [
      { id: 'P1', height: 158, chest: 84, waist: 68 },
      { id: 'P2', height: 165, chest: 90, waist: 74 },
      { id: 'P3', height: 168, chest: 96, waist: 82 },
      { id: 'P4', height: 172, chest: 88, waist: 72 },
      { id: 'P5', height: 175, chest: 102, waist: 88 },
      { id: 'P6', height: 178, chest: 108, waist: 94 },
      { id: 'P7', height: 180, chest: 112, waist: 100 },
      { id: 'P8', height: 170, chest: 118, waist: 108 },
      { id: 'P9', height: 168, chest: 110, waist: 100 },
      { id: 'P10', height: 195, chest: 120, waist: 104 },
    ];
  }

  connectedCallback() {
    if (!this.dataset.chart) return;

    this.chartData = JSON.parse(this.dataset.chart);
    this.wearType  = this.dataset.wearType || 'topwear';

    try {
      const raw = JSON.parse(this.dataset.bodyImages || '[]');
      this.bodyImages = raw.map(img => {
        const { personId, size } = this.parseBodyImageAlt(img.alt || img.url);
        return { ...img, personId, size };
      });
    } catch {
      this.bodyImages = [];
    }

    if (!this.bodyImages.length) {
      this.hideBodyFitFeatures();
    }

    this.renderHeader();
    this.renderTable();
    this.bindToggleEvents();
    this.bindSliderEvents();
    
    if (this.isWithinRange()) {
      this.updateBodyFit();
    }

    setTimeout(() => this.updateAllSlidersUI(), 300);
  }

  roundSmart(num) {
    const rounded = Math.round(num * 10) / 10;
    return rounded % 1 === 0 ? Math.round(rounded) : rounded;
  }

  cmToIn(cm) {
    return Math.round((cm / 2.54) * 10) / 10;
  }

  formatMeasurement(cm) {
    return this.currentUnit === 'in'
      ? `${this.cmToIn(cm).toFixed(1)}in`
      : `${cm}cm`;
  }

  parseBodyImageAlt(alt = '') {
    const base = alt.split('/').pop().replace(/\.[^.]+$/, '');
    const [personId, size] = base.split('_')[0].split('-');
    return { personId: personId?.toUpperCase(), size: size?.toUpperCase() };
  }

  findClosestBodyType(userHeight, userChest, userWaist) {
    const isBottom = this.wearType === 'bottomwear';

    const score = (bt) => {
      const dHeight = Math.abs(bt.height - userHeight);
      const dChest  = Math.abs(bt.chest  - userChest);
      const dWaist  = Math.abs(bt.waist  - userWaist);

      if (isBottom) {
        return dWaist * 100 + dHeight * 20 + dChest * 5;
      } else {
        return dHeight * 100 + dChest * 20 + dWaist * 5;
      }
    };

    let best = null;
    let bestScore = Infinity;

    for (let i = 0; i < this.bodyTypes.length; i++) {
      const bt = this.bodyTypes[i];
      const currentScore = score(bt);

      if (currentScore < bestScore) {
        best = bt;
        bestScore = currentScore;
      }
    }

    return { best, bestScore };
  }

  isWithinRange() {
    const { height, chest, waist } = this.sliderState;

    const getRange = arr => [Math.min(...arr) - 10, Math.max(...arr) + 10];

    const [hMin, hMax] = getRange(this.bodyTypes.map(b => b.height));
    const [cMin, cMax] = getRange(this.bodyTypes.map(b => b.chest));
    const [wMin, wMax] = getRange(this.bodyTypes.map(b => b.waist));

    return (
      height >= hMin && height <= hMax &&
      chest  >= cMin && chest  <= cMax &&
      waist  >= wMin && waist  <= wMax
    );
  }

  renderHeader() {
    const thead = this.querySelector('#sizechart-thead');
    if (!thead) return;

    let html = `<tr><th>${this.unitLabels[this.currentUnit]}</th>`;
    this.chartData.sizes.forEach(size => html += `<th>${size}</th>`);
    html += '</tr>';

    thead.innerHTML = html;
  }

  renderTable() {
    const tbody = this.querySelector('#sizechart-tbody');
    if (!tbody) return;

    let html = '';

    this.chartData.measurements.forEach((m, i) => {
      const letter = this.letters[i] || (i + 1);

      html += `<tr>
        <td class="measurement-label">
          <span class="letter-label">${letter}.</span>
          ${m.label}
        </td>`;

      m.values.forEach(val => {
        const cm = parseFloat(val);
        const inch = this.roundSmart(cm / 2.54);

        html += `<td class="measurement-value" data-cm="${cm}" data-in="${inch}">
          <span class="value">${this.roundSmart(cm)}</span>
        </td>`;
      });

      html += `</tr>`;
    });

    tbody.innerHTML = html;
  }

  updateBodyFit() {
    const { height, chest, waist } = this.sliderState;
    const { best, bestScore } = this.findClosestBodyType(height, chest, waist);

    if (bestScore > 1000) {
      this.showNoMatch();
      return;
    }

    const img = this.bodyImages.find(i => i.personId === best.id);

    const imgEl = this.querySelector('#body-fit-img');
    const modelFit = this.querySelector('.model-fit');
    const heroText = this.querySelector('.results-recommendation-text');
    const meas = this.querySelector('#model-measurements');

    if (!imgEl || !meas) return;

    imgEl.src = img?.url || '';

    heroText.textContent = `Recommended Size ${img?.size || best.id}`;

    modelFit.textContent = `Model wears ${img?.size || best.id}`;

    const heightEl = meas.querySelector('.height-measurement');
    const chestEl = meas.querySelector('.chest-measurement');
    const waistEl = meas.querySelector('.waist-measurement');

    const formatDiff = (diffCm, label) => {
      if (diffCm === 0) {
        return `Model ${label} is Exactly Like Yours`;
      }

      const isIn = this.currentUnit === 'in';

      let val = isIn ? this.cmToIn(Math.abs(diffCm)) : Math.abs(diffCm);
      val = Math.round(val * 10) / 10;

      const unit = isIn ? 'in' : 'cm';

      if (label === 'Height') {
        return `Model is ${val}${unit} ${
          diffCm > 0 ? 'Taller' : 'Shorter'
        } Than You`;
      }

      return `Model ${label} is ${val}${unit} ${
        diffCm > 0 ? 'Larger' : 'Smaller'
      } Than Yours`;
    };

    const map = {
      height: {
        el: heightEl,
        model: best.height,
        user: height,
        label: 'Height'
      },
      chest: {
        el: chestEl,
        model: best.chest,
        user: chest,
        label: 'Chest'
      },
      waist: {
        el: waistEl,
        model: best.waist,
        user: waist,
        label: 'Waist'
      }
    };

    const order =
      this.wearType === 'bottomwear'
        ? ['height', 'waist', 'chest']
        : ['height', 'chest', 'waist'];

    order.forEach((key) => {
      const item = map[key];

      if (!item?.el) return;

      const diff = item.model - item.user;

      item.el.textContent = formatDiff(diff, item.label);

      item.el.classList.remove('text-green', 'text-red');

      if (diff > 0) {
        item.el.classList.add('text-green');
      } else if (diff < 0) {
        item.el.classList.add('text-red');
      }
    });
  }

  showNoMatch() {
    const container = this.querySelector('#body-size-container');

    container.classList.remove('loading');
    container.classList.add('result');
    container.classList.add('no-result');
  }

  updateSliderUI(slider) {
    const wrapper = slider.closest('.slider-track-wrap');
    const valueEl = wrapper?.querySelector('.slider-value');
    if (!valueEl) return;

    const val = +slider.value;

    valueEl.textContent = this.currentUnit === 'in'
      ? `${this.cmToIn(val).toFixed(1)}in`
      : `${val}cm`;

    const percent = (val - slider.min) / (slider.max - slider.min);
    const width = slider.getBoundingClientRect().width;

    if (!width) return;

    const thumbWidth = 40;
    const offset = percent * (width - thumbWidth) + thumbWidth / 2;

    valueEl.style.left = `${offset}px`;
  }

  updateAllSlidersUI() {
    this.querySelectorAll('.body-slider').forEach(slider => {
      this.updateSliderUI(slider);
    });
  }

  bindToggleEvents() {
    this.querySelectorAll('.unit-btn').forEach(btn => {
      btn.onclick = () => this.switchUnit(btn.dataset.unit);
    });

    this.querySelectorAll('.type-btn').forEach(btn => {
      btn.onclick = () => this.switchType(btn.dataset.type);
    });
  }

  bindSliderEvents() {
    ['height','chest','waist'].forEach(field => {
      const slider = this.querySelector(`#slider-${field}`);
      if (!slider) return;

      slider.addEventListener('input', () => {
        this.sliderState[field] = +slider.value;
        this.updateSliderUI(slider);
      });
    });

    const btn = this.querySelector('#find-my-fit-btn');
    if (btn) {
      btn.onclick = () => {
        const container = this.querySelector('#body-size-container');

        container.classList.add('loading');
        container.classList.remove('result');

        setTimeout(() => {
          if (!this.isWithinRange()) {
            this.showNoMatch();
            return;
          }

          this.updateBodyFit();

          container.classList.remove('loading');
          container.classList.add('result');
        }, 300);
      };
    }

    const TryAgainBtn = this.querySelector('#try-again-btn');
    if (TryAgainBtn) {
      TryAgainBtn.onclick = () => {
        const container = this.querySelector('#body-size-container');

        container.classList.remove('result', 'no-result');
      };
    }

    const finishBtn = this.querySelector('#finish-btn');

    if (finishBtn) {
      finishBtn.onclick = () => {
        const targetId = finishBtn.dataset.gotoId;
        const targetEl = document.getElementById(targetId);

        if (!targetEl) return;

        targetEl.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      };
    }
  }

  switchUnit(unit) {
    if (this.currentUnit === unit) return;

    this.currentUnit = unit;

    this.querySelectorAll('.unit-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.unit === unit);
    });

    const th = this.querySelector('#sizechart-thead th:first-child');
    if (th) th.textContent = this.unitLabels[unit];

    this.querySelectorAll('.measurement-value').forEach(cell => {
      const val = unit === 'cm'
        ? parseFloat(cell.dataset.cm)
        : parseFloat(cell.dataset.in);

      cell.querySelector('.value').textContent = this.roundSmart(val);
    });

    this.updateAllSlidersUI();
    this.updateBodyFit();
  }

  switchType(type) {
    this.currentType = type;

    this.querySelectorAll('.type-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.type === type);
    });

    this.querySelectorAll('[data-content-type]').forEach(el => {
      el.classList.toggle('active', el.dataset.contentType === type);
    });

    this.updateAllSlidersUI();
  }

  hideBodyFitFeatures() {
    this.querySelector('.type-selector')?.remove();
    this.querySelectorAll('[data-content-type="body"]').forEach(el => el.remove());
  }
}

if (!customElements.get('sizechart-wrapper')) {
  customElements.define('sizechart-wrapper', SizeChart);
}